import axios from 'axios';
import { googleConfig } from './config';
import { supabase } from '../../supabase';

export async function getAuthUrl(redirectUri, connectionId) {
  const stateObj = {
    service: 'google_analytics',
    connectionId: connectionId
  };
  
  const params = new URLSearchParams({
    client_id: process.env.REACT_APP_GOOGLE_APP_CLIENT_ID,
    redirect_uri: redirectUri,
    scope: googleConfig.requiredScopes.join(' '),
    response_type: 'code',
    access_type: 'offline',
    state: btoa(JSON.stringify(stateObj)),
    prompt: 'consent'
  });

  console.log('Google Auth URL params:', {
    clientId: process.env.REACT_APP_GOOGLE_APP_CLIENT_ID,
    redirectUri,
    scope: googleConfig.requiredScopes.join(' ')
  });

  return `${googleConfig.authEndpoint}?${params.toString()}`;
}

export async function exchangeCodeForToken(code, redirectUri) {
  try {
    console.log('Starting Google token exchange process...');

    // Get access token
    const tokenResponse = await axios.post(googleConfig.tokenEndpoint, {
      client_id: process.env.REACT_APP_GOOGLE_APP_CLIENT_ID,
      client_secret: process.env.REACT_APP_GOOGLE_APP_CLIENT_SECRET,
      redirect_uri: redirectUri,
      grant_type: 'authorization_code',
      code: code
    });

    const { access_token, refresh_token, expires_in } = tokenResponse.data;

    // Get user profile
    const profileResponse = await axios.get('https://www.googleapis.com/oauth2/v2/userinfo', {
      headers: {
        Authorization: `Bearer ${access_token}`
      }
    });

    // Get Analytics accounts first
    const accountsResponse = await axios.get(
      'https://analyticsadmin.googleapis.com/v1beta/accounts',
      {
        headers: {
          Authorization: `Bearer ${access_token}`
        }
      }
    );

    // Get all properties for each account
    const allProperties = [];
    for (const account of accountsResponse.data.accounts || []) {
      try {
        const propertiesResponse = await axios.get(
          `https://analyticsadmin.googleapis.com/v1beta/properties`,
          {
            params: {
              filter: `parent:${account.name}`
            },
            headers: {
              Authorization: `Bearer ${access_token}`
            }
          }
        );
        
        if (propertiesResponse.data.properties) {
          allProperties.push(...propertiesResponse.data.properties);
        }
      } catch (error) {
        console.error(`Error fetching properties for account ${account.name}:`, error);
      }
    }

    return {
      accessToken: access_token,
      refreshToken: refresh_token,
      expiresIn: expires_in,
      userId: profileResponse.data.id,
      userName: profileResponse.data.name,
      userEmail: profileResponse.data.email,
      properties: allProperties.map(property => ({
        id: property.name.split('/').pop(),
        name: property.displayName,
        createTime: property.createTime,
        updateTime: property.updateTime,
        accountId: property.parent
      }))
    };
  } catch (error) {
    console.error('Error exchanging code for token:', error);
    console.error('Error details:', error.response?.data || error.message);
    throw error;
  }
}

export async function testConnection(credentials) {
  try {
    // For GA4, we'll test by trying to get the metadata for the property
    const response = await axios.get(
      `https://analyticsdata.googleapis.com/v1beta/properties/${credentials.propertyId}/metadata`,
      {
        headers: {
          Authorization: `Bearer ${credentials.accessToken}`
        }
      }
    );

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Test connection error:', error);
    return {
      success: false,
      error: error.response?.data?.error?.message || error.message
    };
  }
}

export async function getAnalyticsData(credentials) {
  try {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);

    const response = await axios.post(
      `https://analyticsdata.googleapis.com/v1beta/properties/${credentials.propertyId}:runReport`,
      {
        dateRanges: [
          {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
          }
        ],
        metrics: [
          { name: 'totalUsers' },
          { name: 'screenPageViews' },
          { name: 'averageSessionDuration' },
          { name: 'bounceRate' }
        ]
      },
      {
        headers: {
          Authorization: `Bearer ${credentials.accessToken}`
        }
      }
    );

    const metrics = response.data.rows[0].metricValues;

    return {
      totalUsers: parseInt(metrics[0].value),
      pageViews: parseInt(metrics[1].value),
      avgSessionDuration: parseFloat(metrics[2].value).toFixed(1),
      bounceRate: parseFloat(metrics[3].value).toFixed(1)
    };
  } catch (error) {
    console.error('Error fetching analytics data:', error);
    console.error('Error details:', error.response?.data || error.message);
    throw error;
  }
}

// Add a function to refresh the token
async function refreshAccessToken(refreshToken) {
  try {
    const response = await axios.post(googleConfig.tokenEndpoint, {
      client_id: process.env.REACT_APP_GOOGLE_APP_CLIENT_ID,
      client_secret: process.env.REACT_APP_GOOGLE_APP_CLIENT_SECRET,
      refresh_token: refreshToken,
      grant_type: 'refresh_token'
    });

    return {
      accessToken: response.data.access_token,
      expiresIn: response.data.expires_in
    };
  } catch (error) {
    console.error('Error refreshing token:', error);
    throw error;
  }
}

// Modify getAnalyticsReport to handle token refresh
export async function getAnalyticsReport(credentials) {
  try {
    let accessToken = credentials.accessToken;

    // Try to make the request
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - credentials.days);

      // First get the time series data
      const timeSeriesResponse = await axios.post(
        `https://analyticsdata.googleapis.com/v1beta/properties/${credentials.propertyId}:runReport`,
        {
          dateRanges: [
            {
              startDate: startDate.toISOString().split('T')[0],
              endDate: endDate.toISOString().split('T')[0]
            }
          ],
          metrics: [
            { name: 'totalUsers' },
            { name: 'screenPageViews' },
            { name: 'averageSessionDuration' },
            { name: 'bounceRate' }
          ],
          dimensions: [
            { name: 'date' }
          ],
          orderBys: [
            {
              dimension: {
                dimensionName: 'date'
              }
            }
          ]
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        }
      );

      // Then get the traffic source data
      const trafficSourceResponse = await axios.post(
        `https://analyticsdata.googleapis.com/v1beta/properties/${credentials.propertyId}:runReport`,
        {
          dateRanges: [
            {
              startDate: startDate.toISOString().split('T')[0],
              endDate: endDate.toISOString().split('T')[0]
            }
          ],
          metrics: [
            { name: 'totalUsers' },
            { name: 'screenPageViews' },
            { name: 'averageSessionDuration' },
            { name: 'bounceRate' },
            { name: 'conversions' }
          ],
          dimensions: [
            { name: 'sessionSource' }
          ],
          orderBys: [
            {
              metric: { metricName: 'totalUsers' },
              desc: true
            }
          ],
          limit: 10
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        }
      );

      // Process daily data
      const dailyData = {
        dates: [],
        users: [],
        pageViews: []
      };

      timeSeriesResponse.data.rows.forEach(row => {
        const date = row.dimensionValues[0].value;
        const formattedDate = `${date.slice(6, 8)}/${date.slice(4, 6)}`;
        
        dailyData.dates.push(formattedDate);
        dailyData.users.push(parseInt(row.metricValues[0].value));
        dailyData.pageViews.push(parseInt(row.metricValues[1].value));
      });

      // Process traffic source data
      const trafficSourceData = trafficSourceResponse.data.rows.map(row => ({
        source: row.dimensionValues[0].value || '(direct)',
        users: parseInt(row.metricValues[0].value),
        pageViews: parseInt(row.metricValues[1].value),
        avgSessionDuration: parseFloat(row.metricValues[2].value).toFixed(1),
        bounceRate: parseFloat(row.metricValues[3].value).toFixed(1),
        conversions: parseInt(row.metricValues[4].value)
      }));

      // Get the totals from the last row for the summary metrics
      const lastRow = timeSeriesResponse.data.rows[timeSeriesResponse.data.rows.length - 1];
      const metrics = lastRow.metricValues;

      return {
        totalUsers: parseInt(metrics[0].value),
        pageViews: parseInt(metrics[1].value),
        avgSessionDuration: parseFloat(metrics[2].value).toFixed(1),
        bounceRate: parseFloat(metrics[3].value).toFixed(1),
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        dailyData,
        trafficSourceData
      };
    } catch (error) {
      // If we get a 401, try to refresh the token
      if (error.response?.status === 401 && credentials.refreshToken) {
        console.log('Access token expired, attempting to refresh...');
        const { accessToken: newToken } = await refreshAccessToken(credentials.refreshToken);
        
        // Update the access token in the database
        const { error: updateError } = await supabase
          .from('channels')
          .update({
            credentials: {
              ...credentials,
              accessToken: newToken
            }
          })
          .eq('id', credentials.channelId);

        if (updateError) {
          console.error('Error updating token:', updateError);
          throw updateError;
        }

        // Retry the request with the new token
        return getAnalyticsReport({
          ...credentials,
          accessToken: newToken
        });
      }
      throw error;
    }

  } catch (error) {
    console.error('Error fetching analytics report:', error);
    console.error('Error details:', error.response?.data || error.message);
    throw error;
  }
} 